import React from 'react';
import { Plus, Zap, Play, Pause } from 'lucide-react';
import {
  safeAsync, sp, Badge, emptyMsg, cardWrap, neuShadowSm,
  btnSm, btnPrimary, inp, STEP_ACTIONS, MESSAGING_STEP_NOTE, isMessagingStep
} from './crmShared';

/** A run that has neither finished nor been stopped. */
const ACTIVE_RUN_STATUSES = ['running', 'paused'];

export default function SettingsGroup({
  tab, tags, customFields, workflows, workflowRuns, contacts,
  canCreate, canUpdate, loadExtended, toggleWorkflow, executeWorkflow
}) {
  if (tab === 'tags') {
    return (
      <>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-end' }}>
          {canCreate && <button onClick={safeAsync(async () => { await window.zignagenixAPI.seedDefaultTags(); loadExtended(); })} style={btnSm}><Zap size={13} /> Seed Defaults</button>}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {tags.length === 0 ? (
            <div style={emptyMsg}>No tags defined. Click "Seed Defaults" to create the standard ZignaGenix tags.</div>
          ) : tags.map(t => (
            <div key={t.id} style={{
              background: '#F8F9FA', border: 'none', boxShadow: neuShadowSm, borderRadius: 10,
              padding: '12px 18px', minWidth: 180,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: t.color }} />
                <span style={{ fontWeight: 700, fontSize: 13, color: '#212529' }}>{t.name}</span>
              </div>
              {t.description && <div style={{ fontSize: 11, color: '#6C757D', lineHeight: 1.4 }}>{t.description}</div>}
            </div>
          ))}
        </div>
      </>
    );
  }

  if (tab === 'fields') {
    return (
      <>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-end' }}>
          {canCreate && <button onClick={safeAsync(async () => { await window.zignagenixAPI.seedDefaultCustomFields(); loadExtended(); })} style={btnSm}><Zap size={13} /> Seed Defaults</button>}
        </div>
        <div style={cardWrap}>
          {customFields.length === 0 ? (
            <div style={emptyMsg}>No custom fields. Click "Seed Defaults" to create recommended fields.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#e0e5ec', borderBottom: 'none' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6C757D', fontSize: 11 }}>Label</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6C757D', fontSize: 11 }}>Key</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6C757D', fontSize: 11 }}>Type</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, color: '#6C757D', fontSize: 11 }}>Options</th>
                </tr>
              </thead>
              <tbody>
                {customFields.map((f, i) => (
                  <tr key={f.id} style={{ borderBottom: 'none', background: i % 2 === 1 ? '#F8F9FA' : '#F8F9FA' }}>
                    <td style={{ padding: '10px 16px', fontWeight: 600, color: '#212529' }}>{f.label}</td>
                    <td style={{ padding: '10px 16px', color: '#6C757D', fontFamily: 'monospace', fontSize: 12 }}>{f.field_key}</td>
                    <td style={{ padding: '10px 16px' }}><Badge text={f.field_type} color="#2E8B98" /></td>
                    <td style={{ padding: '10px 16px', fontSize: 12, color: '#6C757D' }}>{f.options || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </>
    );
  }

  if (tab === 'workflows') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)' }}>
        {/* Fixed header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexShrink: 0 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#212529', margin: 0 }}>Patient Journey Workflows ({workflows.length})</h3>
          {canCreate && <button onClick={safeAsync(async () => { await window.zignagenixAPI.seedAllWorkflows(); loadExtended(); })} style={btnSm}><Zap size={13} /> Seed Workflows</button>}
        </div>

        {workflows.length === 0 ? (
          <div style={emptyMsg}>No workflows defined. Click "Seed Workflows" to create the full ZignaGenix automation suite.</div>
        ) : (
          /* Scrollable workflow container */
          <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4, paddingBottom: 24 }}>
            {/* Sort workflows by WF number extracted from name */}
            {[...workflows].sort((a, b) => {
              const na = parseInt((a.name.match(/WF(\d+)/) || [])[1]) || 999;
              const nb = parseInt((b.name.match(/WF(\d+)/) || [])[1]) || 999;
              return na - nb;
            }).map((wf, idx, arr) => {
              const tc = sp(wf.trigger_config, {});
              const steps = sp(wf.steps, []);
              const stops = sp(wf.stop_conditions, []);
              const runs = workflowRuns.filter(r => r.workflow_id === wf.id);
              const wfNum = (wf.name.match(/WF(\d+)/) || [])[1] || (idx + 1);
              const isLast = idx === arr.length - 1;
              return (
                <div key={wf.id} style={{ position: 'relative' }}>
                  {/* Connector line to next workflow */}
                  {!isLast && (
                    <div style={{
                      position: 'absolute', left: 24, top: '100%', width: 2, height: 20,
                      background: 'linear-gradient(to bottom, #256F7A, #c4b5fd)', zIndex: 1,
                    }} />
                  )}
                  {/* Connector arrow */}
                  {!isLast && (
                    <div style={{
                      position: 'absolute', left: 19, top: 'calc(100% + 16px)', width: 0, height: 0,
                      borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                      borderTop: '6px solid #c4b5fd', zIndex: 1,
                    }} />
                  )}

                  <div style={{
                    display: 'flex', gap: 0, marginBottom: isLast ? 0 : 20,
                  }}>
                    {/* Sequence number column */}
                    <div style={{
                      width: 48, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 18,
                    }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%',
                        background: wf.is_active ? 'linear-gradient(135deg, #256F7A, #6d28d9)' : '#d1d5db',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 14, fontWeight: 800, color: '#fff',
                        boxShadow: wf.is_active ? '0 2px 8px rgba(139,92,246,0.3)' : 'none',
                      }}>
                        {wfNum}
                      </div>
                    </div>

                    {/* Card */}
                    <div style={{
                      flex: 1, background: '#F8F9FA', borderRadius: 10,
                      border: 'none',
                      overflow: 'hidden',
                      boxShadow: wf.is_active ? '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.85)' : neuShadowSm,
                    }}>
                      {/* Header */}
                      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid #dce2e8' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#212529', margin: '0 0 3px' }}>{wf.name}</h4>
                            <p style={{ fontSize: 11, color: '#6C757D', margin: 0, lineHeight: 1.4 }}>{wf.description}</p>
                          </div>
                          <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0, marginLeft: 10 }}>
                            <Badge text={wf.is_active ? 'Active' : 'Paused'} color={wf.is_active ? '#62BB46' : '#F26522'} />
                            {canUpdate && (
                              <button onClick={() => toggleWorkflow(wf.id, !wf.is_active)}
                                style={{ ...btnSm, padding: '3px 6px' }} title={wf.is_active ? 'Pause' : 'Activate'}>
                                {wf.is_active ? <Pause size={11} /> : <Play size={11} />}
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Trigger info */}
                        <div style={{ fontSize: 10, color: '#6C757D', marginTop: 6, display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <span><strong>Trigger:</strong> {wf.trigger_type.replace(/_/g, ' ')}</span>
                            {tc.tag && <span><strong>Tag:</strong> {tc.tag}</span>}
                            {tc.calendar && <span><strong>Calendar:</strong> {tc.calendar}</span>}
                            {tc.stage && !tc.filters && <span><strong>Stage:</strong> {tc.stage}</span>}
                            {tc.status && <span><strong>Status:</strong> {tc.status}</span>}
                          </div>
                          {tc.sources && tc.sources.length > 0 && (
                            <div><strong>Sources:</strong> {tc.sources.map(s => s.replace(/_/g, ' ')).join(', ')}</div>
                          )}
                          {tc.filters && (
                            <div><strong>Filters:</strong> {tc.filters.pipeline && `Pipeline = ${tc.filters.pipeline}`}{tc.filters.stage && ` · Stage = ${tc.filters.stage}`}</div>
                          )}
                          {tc.alt_trigger && (
                            <div><strong>Alt Trigger:</strong> {tc.alt_trigger.type?.replace(/_/g, ' ')}{tc.alt_trigger.stage && ` → ${tc.alt_trigger.stage}`}{tc.alt_trigger.field && ` → ${tc.alt_trigger.field}`}</div>
                          )}
                        </div>
                      </div>

                      {/* Steps */}
                      <div style={{ padding: '10px 18px', borderBottom: stops.length > 0 ? '1px solid #f1f5f9' : 'none' }}>
                        <div style={{ fontSize: 10, fontWeight: 600, color: '#6C757D', marginBottom: 5, textTransform: 'uppercase' }}>Steps ({steps.length})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {steps.map((step, i) => {
                            const sa = STEP_ACTIONS[step.action] || { label: step.action, icon: '?' };
                            // ⛔ Card P85: a step that will not send must say so
                            // on the screen where the workflow is read, not only
                            // in the run log after someone has fired it.
                            const notSent = isMessagingStep(step.action);
                            return (
                              <div key={i} style={{
                                display: 'flex', alignItems: 'flex-start', gap: 7, padding: '5px 9px',
                                background: notSent ? '#fff7ed' : step.action === 'wait' ? '#fefce8' : '#f8fafc',
                                borderRadius: 5,
                                border: 'none', boxShadow: neuShadowSm,
                              }}>
                                <span style={{ fontSize: 12, flexShrink: 0 }}>{sa.icon}</span>
                                <span style={{ fontSize: 10, fontWeight: 600, color: '#212529', minWidth: 80, flexShrink: 0 }}>{sa.label}</span>
                                <span style={{ fontSize: 10, color: '#6C757D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {step.label && <span style={{ fontWeight: 600, color: '#2E8B98', marginRight: 3 }}>[{step.label}]</span>}
                                  {step.tag && `Tag: ${step.tag}`}
                                  {step.stage && `Stage: ${step.stage}`}
                                  {step.title && `"${step.title}"`}
                                  {step.template && `Template: ${step.template}`}
                                  {step.timing && ` (${step.timing})`}
                                  {step.message && `"${step.message}"`}
                                  {step.recipients && ` → ${step.recipients.join(', ')}`}
                                  {step.delay_minutes != null && step.delay_minutes !== 0 && `${Math.abs(step.delay_minutes) >= 1440 ? `${Math.round(Math.abs(step.delay_minutes) / 1440)} day(s)` : Math.abs(step.delay_minutes) >= 60 ? `${Math.round(Math.abs(step.delay_minutes) / 60)} hr(s)` : `${Math.abs(step.delay_minutes)} min`}${step.anchor ? ` before ${step.anchor.replace(/_/g, ' ')}` : ''}`}
                                  {step.condition && ` · if ${step.condition.replace(/_/g, ' ')}`}
                                  {step.mode && step.mode !== 'keep' && `Mode: ${step.mode}`}
                                  {step.mode === 'keep' && step.note}
                                  {step.owner_name && ` → ${step.owner_name}`}
                                  {step.owners && step.owners.length > 0 && ` → ${step.owners.join(' / ')}`}
                                  {step.note && step.mode !== 'keep' && ` (${step.note})`}
                                  {notSent && (
                                    <div
                                      data-testid={`step-not-sent-${i}`}
                                      style={{
                                        display: 'block', marginTop: 3, fontSize: 10, fontWeight: 600,
                                        color: '#9A3412', whiteSpace: 'normal',
                                      }}
                                    >
                                      {MESSAGING_STEP_NOTE}
                                    </div>
                                  )}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Stop Conditions */}
                      {stops.length > 0 && (
                        <div style={{ padding: '8px 18px', background: '#fef2f2', borderBottom: '1px solid #dce2e8' }}>
                          <div style={{ fontSize: 10, fontWeight: 600, color: '#DC3545', marginBottom: 3, textTransform: 'uppercase' }}>Stop If</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {stops.map((sc, i) => (
                              <span key={i} style={{ fontSize: 10, color: '#991b1b', background: '#fee2e2', padding: '2px 7px', borderRadius: 4 }}>
                                {sc.type === 'tag_added' && `Tag "${sc.tag}" added`}
                                {sc.type === 'stage_is' && `Stage = "${sc.stage}"`}
                                {sc.type === 'invoice_paid' && 'Invoice paid'}
                                {sc.type === 'appointment_rescheduled' && 'Appt rescheduled'}
                                {sc.type === 'stage_changed_away' && `Leaves "${sc.from}"`}
                                {sc.type === 'stage_changed' && 'Stage changes'}
                                {sc.type === 'appointment_status' && `Appt status = "${sc.status}"`}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Footer */}
                      <div style={{ padding: '8px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fafbfc' }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {canUpdate && contacts.length > 0 && (
                            <>
                              <select id={`wf-sel-${wf.id}`} style={{ ...inp, fontSize: 10, padding: '3px 6px', maxWidth: 160 }}>
                                {contacts.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name}</option>)}
                              </select>
                              <button onClick={() => {
                                const sel = document.getElementById(`wf-sel-${wf.id}`);
                                if (sel) executeWorkflow(wf.id, parseInt(sel.value));
                              }} style={{ ...btnSm, padding: '3px 7px', color: '#0369a1', borderColor: '#bae6fd', fontSize: 10 }}><Play size={10} /> Run</button>
                            </>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                          {runs.length > 0 && <Badge text={`${runs.length} run${runs.length > 1 ? 's' : ''}`} color="#2E8B98" />}
                          {/* ⚠ 'paused' counts as active — a run waiting out a 72-hour
                              delay has neither finished nor stopped. Counting only
                              'running' showed "0 active" for a workflow whose runs were
                              all mid-wait, which is most of their life. */}
                          {runs.filter(r => ACTIVE_RUN_STATUSES.includes(r.status)).length > 0 && (
                            <Badge text={`${runs.filter(r => ACTIVE_RUN_STATUSES.includes(r.status)).length} active`} color="#F26522" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return null;
}
